import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { FormPageLayout } from '@/components/layout/form-page-layout';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { useGo } from '@/hooks/useResourceNavigation';
import { extractCheckResultError } from '@/lib/checkResult';
import {
	getMutationOptions,
	showMutationErrorToast,
	showMutationSuccessToast
} from '@/lib/mutationRefetch';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';

import {
	CUSTOMER_CREATE_MUTATION,
	CUSTOMER_FORM_DATA_QUERY
} from './customers.gql.tsx';
import { buildCreateCustomerInput, generateCustomerId } from './customer-input';
import {
	CustomerForm,
	type CustomerFormValues,
	type SelectOption,
	type CustomerContactFormValue,
	type CustomerAddressFormValue,
	type CustomerBankDetailFormValue,
	type CustomerBeneficiaryFormValue,
	type CustomerVehicleFormValue
} from './customer-form';

/**
 * Complete CloneData interface that includes all customer fields
 * for proper cloning functionality
 */
interface CloneData {
	// Core identity
	isCompany?: boolean;
	firmName?: string | null;
	firstName?: string | null;
	lastName?: string | null;
	email?: string | null;
	fiscalNumber?: string | null;
	taxOffice?: string | null;
	billingLegalName?: string | null;
	billingTradeName?: string | null;
	billingCountryCode?: string | null;
	customerCategoryId?: string | null;

	// Personal information (for individuals)
	dateOfBirth?: string | null;
	gender?: string | null;
	nationality?: string | null;
	idNumber?: string | null;
	idType?: string | null;

	// Corporate information (for companies)
	companyRegistrationNumber?: string | null;
	industry?: string | null;
	companySize?: string | null;

	// Preferences
	preferredLanguage?: string | null;
	notes?: string | null;
	marketingOptIn?: boolean;
	isActive?: boolean;

	// Related entities (cloned without IDs so new ones are created)
	contacts?: CustomerContactFormValue[];
	addresses?: CustomerAddressFormValue[];
	bankDetails?: CustomerBankDetailFormValue[];
	beneficiaries?: CustomerBeneficiaryFormValue[];
	vehicles?: CustomerVehicleFormValue[];
}

interface LocationState {
	cloneData?: CloneData;
}

interface CustomerFormDataQuery {
	customerCategories: Array<{
		customerCategoryId: string;
		description?: string | null;
	}>;
	contactTypes: Array<{
		contactTypeId: number;
		name?: string | null;
		isActive: boolean;
	}>;
	addressTypes: Array<{
		addressTypeId?: number | null;
		name?: string | null;
	}>;
	vehicleTypes: Array<{
		vehicleTypeId: number;
		name?: string | null;
	}>;
}

export const CustomerCreate = () => {
	const { setPageHeader, resetPageHeader } = usePageHeader();
	const go = useGo();
	const location = useLocation();
	const cloneData = (location.state as LocationState | null)?.cloneData;

	const form = useForm<CustomerFormValues>({
		defaultValues: {
			// Core identity
			isCompany: cloneData?.isCompany ?? false,
			firmName: cloneData?.firmName ?? '',
			firstName: cloneData?.firstName ?? '',
			lastName: cloneData?.lastName ?? '',
			email: cloneData?.email ?? '',
			fiscalNumber: cloneData?.fiscalNumber ?? '',
			taxOffice: cloneData?.taxOffice ?? '',
			billingLegalName: cloneData?.billingLegalName ?? '',
			billingTradeName: cloneData?.billingTradeName ?? '',
			billingCountryCode: cloneData?.billingCountryCode ?? '',

			// Preferences
			marketingOptIn: cloneData?.marketingOptIn ?? false,
			notes: cloneData?.notes ?? '',
			isActive: cloneData?.isActive ?? true,
			customerCategoryId: cloneData?.customerCategoryId ?? '',
			preferredLanguage: cloneData?.preferredLanguage ?? '',

			// Corporate information
			companyRegistrationNumber: cloneData?.companyRegistrationNumber ?? '',
			industry: cloneData?.industry ?? '',
			companySize: cloneData?.companySize ?? '',

			// Related entities - strip existing IDs so new ones are created
			contacts: cloneData?.contacts ?? [],
			addresses: cloneData?.addresses ?? [],
			bankDetails: cloneData?.bankDetails ?? [],
			beneficiaries: cloneData?.beneficiaries ?? [],
			vehicles: cloneData?.vehicles ?? []
		}
	});

	const idempotentCustomerId = useMemo(() => generateCustomerId(), []);

	const {
		data: lookupData,
		loading: lookupsLoading,
		error: lookupsError
	} = useQuery<CustomerFormDataQuery>(CUSTOMER_FORM_DATA_QUERY);

	const [createCustomer, { loading: isSubmitting }] = useMutation(
		CUSTOMER_CREATE_MUTATION
	);

	useUnsavedChanges(form.formState.isDirty, {
		isSubmitSuccessful: form.formState.isSubmitSuccessful,
		isSubmitting
	});

	const customerCategoryOptions = useMemo<SelectOption[]>(() => {
		if (!lookupData?.customerCategories) {
			return [];
		}

		return lookupData.customerCategories.map(category => ({
			value: category.customerCategoryId,
			label: category.description ?? 'Customer category'
		}));
	}, [lookupData?.customerCategories]);

	const contactTypeOptions = useMemo<SelectOption[]>(() => {
		if (!lookupData?.contactTypes) {
			return [];
		}

		return lookupData.contactTypes
			.filter(contactType => contactType.isActive)
			.map(contactType => ({
				value: String(contactType.contactTypeId),
				label: contactType.name ?? `Type ${contactType.contactTypeId}`
			}));
	}, [lookupData?.contactTypes]);

	const addressTypeOptions = useMemo<SelectOption[]>(() => {
		if (!lookupData?.addressTypes) {
			return [];
		}

		return lookupData.addressTypes
			.filter(addressType => addressType.addressTypeId != null)
			.map(addressType => ({
				value: String(addressType.addressTypeId),
				label: addressType.name ?? `Type ${addressType.addressTypeId}`
			}));
	}, [lookupData?.addressTypes]);

	const vehicleTypeOptions = useMemo<SelectOption[]>(() => {
		if (!lookupData?.vehicleTypes) {
			return [];
		}

		return lookupData.vehicleTypes.map(vehicleType => ({
			value: String(vehicleType.vehicleTypeId),
			label: vehicleType.name ?? `Type ${vehicleType.vehicleTypeId}`
		}));
	}, [lookupData?.vehicleTypes]);

	useEffect(() => {
		setPageHeader({
			title: 'Create customer',
			description: 'Add a new customer profile',
			showBackButton: true,
			onBackClick: () => go({ to: '/customers' })
		});

		return () => {
			resetPageHeader();
		};
	}, [go, resetPageHeader, setPageHeader]);

	useEffect(() => {
		if (
			form.getValues('customerCategoryId') ||
			customerCategoryOptions.length === 0
		) {
			return;
		}

		form.setValue('customerCategoryId', customerCategoryOptions[0].value);
	}, [customerCategoryOptions, form]);

	const handleSubmit = async (values: CustomerFormValues) => {
		const payload = buildCreateCustomerInput(values, idempotentCustomerId);

		try {
			const { data } = await createCustomer(
				getMutationOptions('customers', { input: payload })
			);

			const message = extractCheckResultError(data?.createCustomer);

			if (message) {
				showMutationErrorToast('customers', 'create', { message });
				return;
			}

			showMutationSuccessToast('customers', 'create');

			// Navigate back with the newly created customer ID for selection
			go({
				to: '/customers',
				state: { selectCustomerId: idempotentCustomerId }
			});
		} catch (error) {
			showMutationErrorToast('customers', 'create', { error });
		}
	};

	return (
		<FormPageLayout>
			{lookupsError ? (
				<Alert variant="destructive">
					<AlertTitle>Lookup data unavailable</AlertTitle>
					<AlertDescription>
						{lookupsError.message ??
							'Required lookup data could not be loaded.'}
					</AlertDescription>
				</Alert>
			) : null}

			<CustomerForm
				mode="create"
				form={form}
				customerCategoryOptions={customerCategoryOptions}
				contactTypeOptions={contactTypeOptions}
				addressTypeOptions={addressTypeOptions}
				vehicleTypeOptions={vehicleTypeOptions}
				isSubmitting={isSubmitting}
				isLoadingLookups={lookupsLoading}
				onSubmit={handleSubmit}
				headerTitle="Create customer"
			/>
		</FormPageLayout>
	);
};
